import { useState, useMemo } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useUserCheck } from '../../hooks/api/useUserCheck'
import { useAllPayments } from '../../hooks/api/useBilling'
import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { FilterDropdown, FilterState } from '../../components/ui/FilterDropdown'

const paymentStatuses = ['All', 'Succeeded', 'Failed', 'Refunded'] as const
type PaymentStatus = typeof paymentStatuses[number]

// Payment status options for billing
const PAYMENT_STATUS_OPTIONS = [
  { value: null, label: 'All Statuses' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' }
]

// Helper to format currency
const formatCurrency = (amount: number) => {
  return `$${(amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// Helper to format date
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const BillingPaymentsPage = () => {
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const { data: payments, isLoading: paymentsLoading } = useAllPayments()
  const [selectedStatus, setSelectedStatus] = useState<PaymentStatus>('All')
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState<FilterState>({
    organization: null,
    status: null,
    priority: null
  })
  const itemsPerPage = 20

  // Get unique organizations from payments
  const organizations = useMemo(() => {
    if (!payments) return []
    const unique = new Set(payments.map((p: any) => p.companies?.name).filter(Boolean))
    return Array.from(unique) as string[]
  }, [payments])

  // Filter payments based on selected status and filters
  const filteredPayments = useMemo(() => {
    let result = payments || []

    // Tab filter
    if (selectedStatus !== 'All') {
      result = result.filter((payment: any) =>
        payment.status.toLowerCase() === selectedStatus.toLowerCase()
      )
    }

    // Organization filter
    if (filters.organization) {
      result = result.filter((payment: any) =>
        payment.companies?.name === filters.organization
      )
    }

    // Status filter (same as tab filter, but from dropdown)
    if (filters.status) {
      result = result.filter((payment: any) =>
        payment.status === filters.status
      )
    }

    return result
  }, [payments, selectedStatus, filters])

  if (isLoading || paymentsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payments...</p>
        </div>
      </div>
    )
  }

  if (error || !userRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-2">User not found in database!</p>
            <p className="text-red-500 text-xs">Please contact support.</p>
          </div>
        </div>
      </div>
    )
  }

  // Pagination
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedPayments = filteredPayments.slice(startIndex, endIndex)

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
          </div>
          <div>
            <FilterDropdown
              filters={filters}
              onFilterChange={setFilters}
              organizations={organizations}
              statusOptions={PAYMENT_STATUS_OPTIONS}
              showPriority={false}
              storageKey="billing-payments-filters"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          {/* Status Filter Tabs */}
          <div className="flex space-x-2 mb-6 border-b border-gray-200">
            {paymentStatuses.map((status) => (
              <button
                key={status}
                onClick={() => {
                  setSelectedStatus(status)
                  setCurrentPage(1)
                }}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  selectedStatus === status
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {status}
              </button>
            ))}
          </div>

          {/* Payments Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedPayments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      No payments found
                    </td>
                  </tr>
                ) : (
                  paginatedPayments.map((payment: any) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.stripe_payment_intent_id.slice(-8).toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.companies?.name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.tier || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          payment.status === 'succeeded'
                            ? 'bg-green-100 text-green-800'
                            : payment.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(payment.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-6 flex items-center justify-between text-sm text-gray-600">
            <div>Showing {startIndex + 1} to {Math.min(endIndex, filteredPayments.length)} of {filteredPayments.length} alerts</div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 border border-gray-300 rounded ${
                  currentPage === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'hover:bg-gray-50'
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 border border-gray-300 rounded ${
                  currentPage === totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
